/**
 * CRM e-mail & outreach: several addresses per person with a status each,
 * address derivation from the account's domain, templates, campaigns sent
 * through the outreach Exim, and the feedback that moves an address's status.
 *
 * The request shapes the web API accepts and the record shapes it returns.
 */
import { z } from 'zod';
import { patchSchema } from './patch.js';
import { isoDate } from './schemas.js';

// ---- address status ----------------------------------------------------------------

/**
 * `candidate`  derived or entered, never mailed
 * `pending`    a mail to it is out; waiting for the log, a bounce or a reply
 * `delivered`  accepted and no bounce within the waiting window
 * `verified`   a reply or auto-reply came back: the mailbox exists
 * `risky`      not rejected, but not to be trusted (catch-all, policy bounce)
 * `rejected`   hard bounce: never mailed again
 */
export const EMAIL_STATUSES = ['candidate', 'pending', 'delivered', 'verified', 'risky', 'rejected'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

/** The address patterns, in the default order they are tried. */
export const EMAIL_PATTERNS = ['first.last', 'f.last', 'first', 'flast', 'last', 'first_last', 'last.first'] as const;
export type EmailPattern = (typeof EMAIL_PATTERNS)[number];

/**
 * One address in a person's `emails` map, keyed by the lower-cased address.
 * Stored as JSON in snake_case, exactly as the requirements name the keys.
 */
export interface EmailEntry {
  status: EmailStatus;
  /** `manual`, `import`, `pattern:<name>` or `reply`. */
  source: string;
  primary: boolean;
  /** Order among candidates: lower is tried first. */
  priority: number;
  first_seen: string;
  updated_at: string;
  last_event: string | null;
  last_event_at: string | null;
  smtp_code: string | null;
  smtp_text: string | null;
  sends: number;
  bounces: number;
  replies: number;
  /** Set by hand: the automatic logic leaves the status alone. */
  locked: boolean;
  /** When the waiting window of the current send started (the 250 from the receiving server). */
  pending_since: string | null;
}
export type EmailMap = Record<string, EmailEntry>;

/** An address as the API returns it. */
export interface CrmEmailAddress {
  address: string; status: EmailStatus; source: string; primary: boolean; priority: number; locked: boolean;
  firstSeen: string; updatedAt: string; lastEvent: string | null; lastEventAt: string | null;
  smtpCode: string | null; smtpText: string | null; sends: number; bounces: number; replies: number; pendingSince: string | null;
}

/** One domain in an account's `email_pattern` map. */
export interface DomainPattern {
  domain: string;
  pattern: EmailPattern | null;
  confidence: 'guessed' | 'verified' | null;
  catch_all: boolean;
  evidence_contact_id: string | null;
  /** Per pattern: +1 per verified address, -1 per rejected one. Reorders the candidates. */
  scores: Partial<Record<EmailPattern, number>>;
  /** Result of the last MX lookup; `none` blocks candidate generation. */
  mx: 'ok' | 'none' | null;
  mx_checked_at: string | null;
  /** Set when a hard bounce hit the verified pattern: a person should look, nothing changes by itself. */
  warning: string | null;
}
export type DomainPatternMap = Record<string, DomainPattern>;

// ---- person addresses ------------------------------------------------------------------

const address = z.string().trim().toLowerCase().email().max(254);

export const crmEmailAddSchema = z.object({
  address,
  /** Defaults to `candidate`; any other status locks the address, since a person decided it. */
  status: z.enum(EMAIL_STATUSES).optional(),
  primary: z.boolean().optional(),
  locked: z.boolean().optional(),
  source: z.enum(['manual', 'import']).default('manual'),
});
export type CrmEmailAddDto = z.input<typeof crmEmailAddSchema>;

export const crmEmailUpdateSchema = z.object({
  primary: z.literal(true).optional(),
  locked: z.boolean().optional(),
  /** Setting a status by hand locks the address too. */
  status: z.enum(EMAIL_STATUSES).optional(),
});
export type CrmEmailUpdateDto = z.input<typeof crmEmailUpdateSchema>;

export interface CrmDeriveResult {
  domain: string | null;
  /** Why nothing was generated, when nothing was. */
  skipped: null | 'no_domain' | 'no_mx' | 'no_name' | 'has_usable_address';
  created: string[];
  pattern: { pattern: EmailPattern; confidence: 'guessed' | 'verified' } | null;
}

export interface CrmEmailEvent {
  id: string; address: string | null; source: string; signal: string; occurredAt: string; receivedAt: string;
  fromStatus: EmailStatus | null; toStatus: EmailStatus | null; applied: boolean; detail: string | null; sendId: string | null;
}

// ---- settings --------------------------------------------------------------------------

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time is HH:MM');

/**
 * Per-company outreach settings. Every value has a default, so a company that
 * never saved them sends with the defaults below.
 */
export const outreachSettingsSchema = z.object({
  /** Mails per hour in total, and per receiving domain. */
  maxPerHour: z.number().int().min(1).max(10_000).default(50),
  maxPerDomainPerHour: z.number().int().min(1).max(1_000).default(5),
  /** Hard ceiling per day, whatever the warm-up curve says. */
  maxPerDay: z.number().int().min(1).max(100_000).default(2_000),
  /**
   * Warm-up for a new sending domain: the day cap on day N after `startDate`
   * is `curve[N]`, the last value from then on. No start date: no warm-up.
   */
  warmup: z.object({
    startDate: isoDate.nullable().default(null),
    curve: z.array(z.number().int().min(1)).min(1).max(60).default([20, 40, 80, 150, 300, 500, 1_000, 2_000]),
  }).default({ startDate: null, curve: [20, 40, 80, 150, 300, 500, 1_000, 2_000] }),
  /** When mails go out: ISO weekdays 1 (Mon) to 7 (Sun), local time of the recipient. */
  window: z.object({
    days: z.array(z.number().int().min(1).max(7)).min(1).default([1, 2, 3, 4, 5]),
    from: hhmm.default('08:00'),
    to: hhmm.default('17:00'),
    /** Used when the recipient's time zone cannot be told from the account's country. */
    timezone: z.string().default('Europe/Amsterdam'),
    /** Use the account country's time zone when it has a single one. */
    recipientTimezone: z.boolean().default(true),
  }).default({ days: [1, 2, 3, 4, 5], from: '08:00', to: '17:00', timezone: 'Europe/Amsterdam', recipientTimezone: true }),
  /** Hours without a bounce after the 250 before an address counts as delivered. */
  waitHours: z.number().int().min(1).max(24 * 14).default(48),
  /** Candidates generated per person at most. */
  maxCandidates: z.number().int().min(1).max(20).default(4),
  /** The order patterns are tried in. */
  patternOrder: z.array(z.enum(EMAIL_PATTERNS)).min(1).default([...EMAIL_PATTERNS]),
  /** Pause candidate sends when the hard-bounce rate goes over this (0.02 = 2%). */
  maxHardBounceRate: z.number().min(0).max(1).default(0.02),
  /** Pause all outreach when the complaint rate goes over this (0.001 = 0.1%). */
  maxComplaintRate: z.number().min(0).max(1).default(0.001),
  /** Sends a day needs before the daily bounce rate is judged; an hour is judged from 4× this. */
  minVolumeForRates: z.number().int().min(1).default(50),
  /** Months that rejected addresses and raw log events are kept. */
  retentionMonths: z.number().int().min(1).max(120).default(12),
  /**
   * Where a copy of every reply goes, with Reply-To set to the person who
   * answered, so the team can answer from its own mailbox. Null: replies are
   * only in the console.
   */
  replyForwardTo: z.string().trim().toLowerCase().email().max(254).nullable().default(null),
});
export type OutreachSettings = z.output<typeof outreachSettingsSchema>;
/**
 * A change to the settings: only the fields given. Not `.partial()`: in zod 4
 * that keeps each field's default, so a patch of one field would come back
 * with every other field at its default and reset the stored settings.
 */
export const updateOutreachSettingsSchema = patchSchema(outreachSettingsSchema);
export type UpdateOutreachSettingsDto = z.input<typeof updateOutreachSettingsSchema>;

export const OUTREACH_PAUSE_SCOPES = ['candidates', 'all'] as const;
export type OutreachPauseScope = (typeof OUTREACH_PAUSE_SCOPES)[number];

export interface OutreachStatus {
  settings: OutreachSettings;
  paused: null | { scope: OutreachPauseScope; reason: string; at: string };
  sendingDomain: string;
  enabled: boolean;
  today: { sends: number; hardBounces: number; complaints: number; dayCap: number };
  lastHour: { sends: number };
}

// ---- templates ---------------------------------------------------------------------------

export const OUTREACH_LANGUAGES = ['nl', 'en'] as const;
export type OutreachLanguage = (typeof OUTREACH_LANGUAGES)[number];

/** Every variable a template may use. Anything else is refused when it is saved. */
export const OUTREACH_VARIABLES = [
  'contact.first_name', 'contact.last_name', 'contact.name', 'contact.role_title',
  'account.name', 'account.website', 'sender.name', 'sender.email', 'unsubscribe_url',
] as const;
export type OutreachVariable = (typeof OUTREACH_VARIABLES)[number];

export const outreachTemplateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  language: z.enum(OUTREACH_LANGUAGES).default('nl'),
  subject: z.string().trim().min(1).max(300),
  bodyHtml: z.string().trim().min(1).max(200_000),
  /** Derived from the HTML when left empty. */
  bodyText: z.string().trim().max(200_000).optional().nullable(),
  senderName: z.string().trim().min(1).max(200),
  senderEmail: z.string().trim().toLowerCase().email().max(254),
});
export type OutreachTemplateDto = z.input<typeof outreachTemplateSchema>;
export const updateOutreachTemplateSchema = outreachTemplateSchema.partial();
export type UpdateOutreachTemplateDto = z.input<typeof updateOutreachTemplateSchema>;

export interface OutreachTemplate {
  id: string; name: string; language: OutreachLanguage; subject: string; bodyHtml: string; bodyText: string | null;
  senderName: string; senderEmail: string; version: number; archivedAt: string | null; createdAt: string; updatedAt: string;
}

export const outreachPreviewSchema = z.object({ personId: z.string().uuid() });
export const outreachTestSendSchema = z.object({
  /** Fills the variables; without it a sample contact is used. */
  personId: z.string().uuid().optional(),
  /** Defaults to the signed-in user's own address. */
  to: z.string().trim().toLowerCase().email().optional(),
});

export interface OutreachRendered { subject: string; html: string; text: string; missing: string[] }

// ---- campaigns ------------------------------------------------------------------------------

export const OUTREACH_CAMPAIGN_STATUSES = ['active', 'paused', 'done', 'cancelled'] as const;
export type OutreachCampaignStatus = (typeof OUTREACH_CAMPAIGN_STATUSES)[number];

/**
 * `pending`    waiting for its turn (throttle, window, cap) or for the next candidate
 * `in_flight`  one mail is out to one address
 * `delivered`  an address took it without a bounce: done
 * `replied`    the person replied: done
 * `exhausted`  no address left to try
 * `skipped`    not to be mailed (opt-out, suppressed, missing variable, policy bounce)
 * `cancelled`  the campaign was cancelled before this person was reached
 */
export const OUTREACH_MEMBER_STATUSES = ['pending', 'in_flight', 'delivered', 'replied', 'exhausted', 'skipped', 'cancelled'] as const;
export type OutreachMemberStatus = (typeof OUTREACH_MEMBER_STATUSES)[number];

export const outreachCampaignSchema = z.object({
  name: z.string().trim().min(1).max(200),
  templateId: z.string().uuid(),
  personIds: z.array(z.string().uuid()).max(5_000).default([]),
});
export type OutreachCampaignDto = z.input<typeof outreachCampaignSchema>;
export const outreachCampaignMembersSchema = z.object({ personIds: z.array(z.string().uuid()).min(1).max(5_000) });

export interface OutreachCampaignMember {
  id: string; personId: string; personName: string; accountName: string; status: OutreachMemberStatus; detail: string | null;
  currentSendId: string | null; currentAddress: string | null; updatedAt: string;
}

export interface OutreachCampaign {
  id: string; name: string; templateId: string; templateName: string; status: OutreachCampaignStatus;
  counts: Partial<Record<OutreachMemberStatus, number>>; createdAt: string; updatedAt: string;
}

export interface OutreachCampaignDetail extends OutreachCampaign { members: OutreachCampaignMember[] }

// ---- sends -----------------------------------------------------------------------------------

export const OUTREACH_SEND_KINDS = ['campaign', 'test'] as const;
export type OutreachSendKind = (typeof OUTREACH_SEND_KINDS)[number];

/**
 * `queued`     created, not handed to Exim yet
 * `sent`       Exim took it (submission accepted)
 * `accepted`   the receiving server answered 250
 * `deferred`   the receiving server answered 4xx; Exim retries
 * `delivered`  the waiting window passed without a bounce
 * `bounced`    a 5xx, in the log or as a DSN
 * `failed`     Exim could not take it after retries
 * `blocked`    refused before sending (missing variable, opt-out, suppression)
 * `cancelled`  dropped from the queue (campaign cancelled, opt-out)
 */
export const OUTREACH_SEND_STATUSES = ['queued', 'sent', 'accepted', 'deferred', 'delivered', 'bounced', 'failed', 'blocked', 'cancelled'] as const;
export type OutreachSendStatus = (typeof OUTREACH_SEND_STATUSES)[number];

export interface OutreachSend {
  id: string; kind: OutreachSendKind; personId: string | null; personName: string | null; campaignId: string | null;
  templateId: string | null; templateVersion: number | null; address: string; subject: string; status: OutreachSendStatus;
  smtpCode: string | null; smtpText: string | null; detail: string | null;
  createdAt: string; sentAt: string | null; deliveredAt: string | null; bouncedAt: string | null;
}

export const outreachSendListQuery = z.object({
  personId: z.string().uuid().optional(),
  campaignId: z.string().uuid().optional(),
  status: z.enum(OUTREACH_SEND_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

// ---- suppression and review --------------------------------------------------------------------

export const outreachSuppressionSchema = z.object({
  email: z.string().trim().toLowerCase().email().optional(),
  domain: z.string().trim().toLowerCase().regex(/^(?=.{1,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/, 'Not a domain name').optional(),
  reason: z.string().trim().min(1).max(500).default('Blocklist'),
}).refine((v) => !!v.email !== !!v.domain, 'Give either an e-mail address or a domain');

export interface OutreachSuppression {
  id: string; kind: string; domain: string | null; hasEmail: boolean; reason: string; requestedVia: string; at: string;
}

export interface OutreachReviewItem {
  id: string; source: string; signal: string; detail: string | null; receivedAt: string; raw: string | null;
}

// ---- internal ingest (Exim sidecar) ------------------------------------------------------------

export const EXIM_EVENT_TYPES = ['arrival', 'delivered', 'deferred', 'failed'] as const;

export const eximEventSchema = z.object({
  type: z.enum(EXIM_EVENT_TYPES),
  queueId: z.string().min(1).max(64),
  at: z.string().datetime({ offset: true }),
  /** Message-ID on arrival lines (`id=`), without angle brackets. */
  messageId: z.string().max(998).optional().nullable(),
  recipient: z.string().max(998).optional().nullable(),
  /** Envelope sender: `P=` on deliveries (+return_path_on_delivery), `<=` on arrivals. */
  returnPath: z.string().max(998).optional().nullable(),
  smtpCode: z.string().max(20).optional().nullable(),
  smtpText: z.string().max(2_000).optional().nullable(),
  raw: z.string().max(8_000),
});
export type EximEvent = z.output<typeof eximEventSchema>;
export const eximIngestSchema = z.object({ events: z.array(eximEventSchema).max(2_000) });
